/**
 * Mixed 3D Viewer · Dashboard (supervision platform host).
 *
 * Layout-driven, polymorphic shell: the grid is described by a
 * `DashboardLayout` JSON, each cell mounts one `DashboardWidget` instance from
 * `@ais/dashboard-widgets`. The dashboard owns the grid and edit-mode chrome;
 * the widgets own their cell content.
 *
 * Layout sources, in order:
 *   site build (SBASE set):   `?layout=<url>` → SERVER board (the ui-seed's
 *     `dashboard.board` filename, fetched from `files/dashboards/`) →
 *     localStorage (offline fallback) → `defaultLayout()`.
 *   default build (no SBASE): `?layout=<url>` → `defaultLayout()` — NO
 *     localStorage at all: without a backend there is no durable store, and a
 *     sometimes-there browser cache reads as data loss, so the default build
 *     is deliberately stateless between reloads.
 *
 * Save/Load on a site build persist boards as named JSONs on the site NAS and
 * record the CURRENT board's filename in the site config (PUT
 * api/config/dashboard), so entering the dashboard restores the same board on
 * any browser and after restarts. Default build keeps download / file-pick.
 */
import './dashboard.css';
import '@ais/viewer-ui/styles.css';
import '@ais/dashboard-widgets/src/builtin/viewer-snapshot/styles.css';

import {
  DashboardWidgetHost,
  registerBuiltins,
  DASHBOARD_WIDGETS_VERSION,
  type DashboardWidget,
  type DashboardWidgetInstance,
  type DashboardCtx,
  type CellFeed,
  type CellFeedInput,
  type CellSummary,
  type ResourceEntry,
  type ResourceRef,
} from '@ais/dashboard-widgets';

import { ResultPickerListing, ResultPicker, createSiteAssetPicker, Popup } from '@ais/viewer-ui';
import { createContentAddressedUploader } from '@ais/viewer-core';

import {
  clampRect,
  computeGrid,
  defaultLayout,
  findFreeRect,
  isLayoutJson,
  migrateLayout,
  moveTab,
  newTab,
  rectIsFree,
  DEFAULT_GRID,
  type DashboardTab,
  type CellRect,
  newCellId,
  newResourceId,
  serialiseLayout,
  type DashboardCell,
  type DashboardLayout,
} from './layout';

// Injected by vite.config.ts. SERVER base for the per-site /files bucket that
// backs board save/load + the per-cell result picker. Default '/' → after the
// trailing-slash strip becomes '' (falsy) so the whole feature is GATED OFF and
// Save/Load keep today's download / file-pick behavior (the fallback contract).
// VIEWER_BASE=./ → '.' (truthy) → app-relative, so PUT/GET land on THIS site via
// /s/<token>/dashboard/files/. Mirrors the admin app's SBASE exactly.
declare const __SERVER_BASE__: string;
const SBASE = __SERVER_BASE__.replace(/\/$/, '');

// Site root (<origin><SITE_BASE>/) — the app is served at <site>/dashboard/, so
// its parent is the site base; the backend API hangs off it at api/. Mirrors the
// admin app's SITE_ROOT. Only meaningful (and only used) when SBASE is set.
const SITE_ROOT = new URL('../', document.baseURI);
const DASH_CONFIG_URL = new URL('api/config/dashboard', SITE_ROOT).href;

// ---------------------------------------------------------------------------
// CLIENT VIEW MODE (?view=1) — the read-only consumer of the Publish pointer.
// Loads the PUBLISHED board (never the operator's working board), drops cells
// the operator marked `viewerHidden`, hides every piece of edit chrome (CSS
// class on <body>), and never persists anything: a viewer panning a map must
// change nothing for anyone. Same build, same widgets — live cells stay live.
// ---------------------------------------------------------------------------
const VIEW_MODE = new URLSearchParams(window.location.search).has('view');

// The tiny saved-board index must get onto the connection before scene assets.
// Never await it: cell loads remain parallel. Standalone/client views do not
// list saved boards and allocate no cache. No board contents are prefetched.
const boardListing = SBASE && !VIEW_MODE
  ? new ResultPickerListing(new URL(`${SBASE}/files/dashboards/`, document.baseURI).href)
  : undefined;
function warmBoardListing(): void {
  void boardListing?.refresh().catch(() => { /* picker offers a visible retry */ });
}
// Self-contained inline previews have no saved-board directory to warm.
if (!/^data:/i.test(new URLSearchParams(window.location.search).get('layout') ?? '')) warmBoardListing();
let boardPickerOpen = false;

/** View-mode board: config/dashboard → `published` → that file, with
 *  viewerHidden cells removed. Null = nothing published (caller shows why). */
async function loadPublishedBoard(): Promise<DashboardLayout | null> {
  try {
    const cfg = await fetch(DASH_CONFIG_URL);
    if (!cfg.ok) throw new Error(`config HTTP ${cfg.status}`);
    const j = (await cfg.json()) as { published?: unknown };
    const name = typeof j.published === 'string' ? j.published.trim() : '';
    if (!name) return null;
    const r = await fetch(`${SBASE}/files/dashboards/${encodeURIComponent(name)}`);
    if (!r.ok) throw new Error(`published board ${name}: HTTP ${r.status}`);
    const json = await r.json();
    if (!isLayoutJson(json)) return null;
    const lay = migrateLayout(json);
    for (const tab of lay.tabs) tab.cells = tab.cells.filter((c) => !c.viewerHidden);
    return lay;
  } catch (e) {
    console.warn(`[dashboard] published board load failed: ${(e as Error).message}`);
    return null;
  }
}

// Injected by vite.config.ts at build time (date + short git hash) — the same
// version-badge recipe as the admin app, so a deploy is verifiable at a glance.
declare const __BUILD_STAMP__: string;
const BUILD_LABEL = `v${DASHBOARD_WIDGETS_VERSION} · ${__BUILD_STAMP__}`;
console.info(`[dashboard build] ${BUILD_LABEL}`);

// Layout persistence key. localStorage is per-ORIGIN, so on a base-portable
// site deployment the SAME build serves several mounts (the Dashboard tab at
// /dashboard/ and the operator UI's inline PREVIEW copy at /nas/_viewer/preview/)
// and a fixed key BLEEDS state between them: the preview's teardown flush
// overwrites the board the Dashboard tab restores on next open. Namespace the
// key by the app's pathname on portable builds; the DEFAULT build keeps the
// original fixed key (fallback contract — existing deployments keep their state).
const LAYOUT_STORAGE_KEY = SBASE
  ? `@ais/dashboard:layout:${location.pathname}`
  : '@ais/dashboard:layout';

/** `yyyymmdd-hhmm` local time — default board-name suffix (mirrors the editor). */
function boardStamp(): string {
  const d = new Date();
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}
/** Make an operator-typed board name safe for a URL path segment (mirrors the
 *  editor's sanitizeCheckpointName): drop a trailing `.json`, keep Unicode
 *  letters / digits plus `._-`, collapse the rest to `-`, trim leading/trailing
 *  separators. '' → caller's default. */
function sanitizeBoardName(raw: string): string {
  return raw
    .trim()
    .replace(/\.json$/i, '')
    .replace(/[^\p{L}\p{N}\p{M}._-]+/gu, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
}

/** Per-cell "pick a result": lists the SAME checkpoint store the editor saves
 *  into (nginx aliases admin/files and dashboard/files to one <NAS>/viewer/files
 *  store) and resolves the chosen snapshot's URL. Wired onto every cell's ctx
 *  only when SBASE is set. */
async function pickCheckpointUrl(): Promise<string | null> {
  return ResultPicker(`${SBASE}/files/checkpoints/`, {
    title: 'Pick a result',
    filter: (n) => n.toLowerCase().endsWith('.json'),
  });
}

interface MountedCell {
  cell: DashboardCell;
  instance: DashboardWidgetInstance | null;
  root: HTMLElement;       // outer .cell wrapper
  hostEl: HTMLElement;     // inner .cell-host (widget mount target)
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

// The registry follows the SBASE seam, like the admin's (PR 16) and the store's.
// Standalone (SBASE '') keeps '/plugins/registry.json'; a site build reads its
// HOST's mirror, which is the only copy that can answer for that site and the
// only one that survives its uplink being down. Without this a site dashboard
// silently found no widgets and every marketplace cell rendered "Unknown widget
// type" — the board is not wrong, the lookup was.
const host = new DashboardWidgetHost({
  engineVersion: DASHBOARD_WIDGETS_VERSION,
  ...(SBASE ? { registryUrl: `${SBASE}/plugins/registry.json` } : {}),
});
registerBuiltins(host);

let layout: DashboardLayout = defaultLayout();
const mounted = new Map<string, MountedCell>();

/**
 * Tabs the user LEFT, kept alive and hidden instead of torn down — least
 * recent first. Switching tabs used to dispose every widget and rebuild the
 * next tab from nothing: each viewer cell re-downloaded its scene and made a
 * new WebGL context, and the old ones lingered until GC, so a tab switch took
 * 1 s or much longer depending on what the collector had got to. `mounted`
 * stays "the cells on screen"; a parked tab's cells are hidden
 * (`display:none`) and told `setVisible(false)` so viewers stop drawing and
 * videos stop decoding. Capped: a board of a dozen viewer tabs would
 * otherwise hold a dozen live GL contexts (Chrome evicts past ~16).
 */
const parked = new Map<string, MountedCell[]>();
const MAX_PARKED_TABS = 3;

function tellVisible(m: MountedCell, visible: boolean): void {
  const inst = m.instance as { setVisible?: (v: boolean) => void } | null;
  try { inst?.setVisible?.(visible); } catch (e) { console.warn(`[dashboard] setVisible: ${(e as Error).message}`); }
}

function disposeCells(cells: MountedCell[]): void {
  for (const m of cells) {
    try { m.instance?.dispose(); } catch (e) { console.error(e); }
    dropFeed(m.cell.id);
    m.root.remove();
  }
}

/** Hide the on-screen tab's cells and keep them for a quick return. */
function parkActiveTab(): void {
  finishDrag(false);
  const cells = [...mounted.values()];
  mounted.clear();
  for (const m of cells) {
    m.root.style.display = 'none';
    tellVisible(m, false);
  }
  const cur = activeTab().id;
  parked.delete(cur);
  parked.set(cur, cells);
  while (parked.size > MAX_PARKED_TABS) {
    const [oldest, old] = parked.entries().next().value as [string, MountedCell[]];
    parked.delete(oldest);
    disposeCells(old);
  }
}

/** The tab currently on screen. Every cell operation is scoped to it — a board
 *  is a stack of independent grids, not one grid with filtered cells. */
/** Reduce cols:rows to lowest terms, then say it the way displays are sold:
 *  7:3 IS 21:9, and nobody calls an ultrawide "seven by three". */
const SHAPE_ALIASES: Record<string, string> = { '7:3': '21:9', '43:18': '21:9' };

function shapeLabel(cols: number, rows: number): string {
  const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
  const g = Math.max(1, gcd(Math.round(cols), Math.round(rows)));
  const reduced = `${Math.round(cols) / g}:${Math.round(rows) / g}`;
  return SHAPE_ALIASES[reduced] ?? reduced;
}

function activeTab(): DashboardTab {
  const found = layout.tabs.find((t) => t.id === layout.activeTabId);
  if (found) return found;
  if (!layout.tabs.length) layout.tabs.push(newTab('Main'));
  layout.activeTabId = layout.tabs[0].id;
  return layout.tabs[0];
}

/** Live grid geometry, recomputed on every resize (see {@link applyGridSize}). */
let gridGeom = {
  cols: DEFAULT_GRID.minCols,
  rows: DEFAULT_GRID.minRows,
  colW: DEFAULT_GRID.cellPx,
  rowH: DEFAULT_GRID.cellPx,
  gap: 8,
};

const grid = document.getElementById('dash-grid')!;
const drawer = document.getElementById('config-drawer') as HTMLElement;

// The grid is viewport-derived, so it must be re-resolved whenever the
// container changes size — a maximise, a sidebar toggle, a projector switch.
document.getElementById('dash-tabs-handle')
  ?.addEventListener('click', () => setTabsOpen(!tabsOpen()));

// A finger that has held a tab owns the gesture: the rail must not scroll under
// it, and the long-press menu must not open over it. These sit on the rail for
// good (not per drag) because Chrome decides at touchstart whether a touch
// listener can cancel the scroll.
{
  const rail = document.getElementById('dash-tabs');
  rail?.addEventListener('touchmove', (e) => {
    if (tabDrag?.armed && e.cancelable) e.preventDefault();
  }, { passive: false });
  rail?.addEventListener('contextmenu', (e) => {
    if (tabDrag) e.preventDefault();
  });
}

const gridResize = new ResizeObserver(() => {
  applyGridSize();
  syncToolbarValues();
});
gridResize.observe(document.getElementById('dash-stage') ?? grid);

void bootstrap();

async function bootstrap(): Promise<void> {
  if (VIEW_MODE) document.body.classList.add('view-mode');
  layout = await loadInitialLayout();
  applyGridSize();
  syncToolbarValues();
  renderLayout();
  // Rename the board in place — the same double-click gesture as a tab, edit
  // mode only. Empty clears back to the default wording.
  const h1 = document.querySelector<HTMLHeadingElement>('#dash-header h1');
  if (h1 && !VIEW_MODE) {
    h1.title = 'Double-click in edit mode to rename the board — the client view shows this title';
    h1.addEventListener('dblclick', () => {
      if (!document.body.classList.contains('edit-mode')) return;
      const next = window.prompt('Board title', layout.title ?? '');
      if (next == null) return;
      const trimmed = next.trim();
      if (trimmed) layout.title = trimmed;
      else delete layout.title;
      renderHeader();
      persistLayout();
    });
  }

  // Best-effort marketplace widget load. Built-ins render immediately so the
  // initial paint isn't blocked by network; any new types appear in the
  // edit-mode dropdown after this resolves. Cells whose type was unknown at
  // initial render time get re-mounted once their widget is now registered.
  void host
    .loadFromRegistry()
    .then((r) => {
      if (r.loaded.length > 0) console.info(`[dashboard] loaded widgets: ${r.loaded.join(', ')}`);
      if (r.failed.length > 0) console.warn(`[dashboard] failed widgets: ${r.failed.join(', ')}`);
      remountNewlyKnownCells();
    })
    .catch((e) =>
      console.warn(`[dashboard] widget registry load failed: ${(e as Error).message}`),
    );

  wireToolbar();
  wireGridDrop();

  // Flush runtime widget state to localStorage on page teardown so a hard
  // reload (F5 / Ctrl+R / tab close) preserves things like Map2d pins or the
  // CCTV playhead. Without this, runtime mutations only survive an explicit
  // Save click because every other persistLayout call sits on a user action.
  // `pagehide` fires more reliably than `beforeunload` (Safari/iOS).
  window.addEventListener('pagehide', () => {
    try { persistLayout(); } catch { /* ignore */ }
  });

  // Version badge — same chip as the admin app's, bottom-left so it never
  // collides with the top toolbar. pointer-events:none = purely informational.
  const verBadge = document.createElement('div');
  verBadge.textContent = BUILD_LABEL;
  verBadge.title = 'Dashboard version + build time (Malaysia time) / commit. Bumps every deploy.';
  verBadge.style.cssText =
    'position:fixed; bottom:6px; left:8px; z-index:9999; pointer-events:none;' +
    'font:11px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.2px;' +
    'color:#cfe0ff; background:rgba(20,28,48,0.78); border:1px solid rgba(120,150,255,0.45);' +
    'padding:2px 7px; border-radius:4px;';
  document.body.appendChild(verBadge);

  // Expose for console debugging — and as the read path for in-window widgets
  // (the LLM agent reads the board through these). `dashboardSyncState` pulls
  // every widget's live state in first, so a reader sees the board as it is now
  // rather than as it was at the last save.
  const w = window as unknown as {
    dashboardLayout?: DashboardLayout;
    dashboardSyncState?: () => DashboardLayout;
  };
  w.dashboardLayout = layout;
  w.dashboardSyncState = () => { syncCellState(); return layout; };
}

function syncToolbarValues(): void {
  const colsInp = document.getElementById('dash-cols') as HTMLInputElement | null;
  if (colsInp) colsInp.value = String(layout.grid.minCols);
  const rowsInp = document.getElementById('dash-rows') as HTMLInputElement | null;
  if (rowsInp) rowsInp.value = String(layout.grid.minRows);
  const dims = document.getElementById('dash-dims');
  if (dims) {
    dims.textContent = `${gridGeom.cols} × ${gridGeom.rows} · cell `
      + `${Math.round(gridGeom.colW)}×${Math.round(gridGeom.rowH)}px`;
    dims.title = 'The grid always fills the window — the cells stretch, so a widget spanning half '
      + 'the cells is half the board on any screen. Nothing letterboxes and nothing scrolls.';
  }
}


// ---------------------------------------------------------------------------
// Layout sources
// ---------------------------------------------------------------------------

async function loadInitialLayout(): Promise<DashboardLayout> {
  const urlParam = new URLSearchParams(window.location.search).get('layout');
  if (urlParam) {
    try {
      const r = await fetch(urlParam);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      if (isLayoutJson(json)) return migrateLayout(json);
      console.warn(`[dashboard] ?layout=${urlParam} did not return a valid layout`);
    } catch (e) {
      console.warn(`[dashboard] could not fetch layout from ${urlParam}: ${(e as Error).message}`);
    }
  }

  // Default build: deliberately stateless — no server, no cache (see header).
  if (!SBASE) return defaultLayout();

  // Client view: published or nothing — the working board and the local cache
  // are the OPERATOR's, and a client must never see either.
  if (VIEW_MODE) {
    const published = await loadPublishedBoard();
    if (published) return published;
    const empty = defaultLayout();
    empty.tabs.forEach((t) => { t.cells = []; t.name = 'Not published'; });
    document.getElementById('dash-header')?.insertAdjacentHTML('beforeend',
      '<p class="view-empty">Nothing published yet — an operator publishes a board from the dashboard\'s Layout menu.</p>');
    return empty;
  }

  // Site build: the ui-seed's current board is the durable source of truth.
  const server = await loadServerBoard();
  if (server) return server;

  // Server has no board yet (or is unreachable): fall back to the local cache
  // so an operator's in-progress board survives the upgrade / an API blip.
  const stored = localStorage.getItem(LAYOUT_STORAGE_KEY);
  if (stored) {
    try {
      const json = JSON.parse(stored);
      if (isLayoutJson(json)) return migrateLayout(json);
    } catch {
      // fall through
    }
  }

  return defaultLayout();
}

/** Fetch the seed-pointed current board: GET api/config/dashboard → `{ board }`
 *  (a filename under files/dashboards/), then fetch + validate that JSON.
 *  Any miss (no pointer yet, file gone, invalid) → null, caller falls through. */
async function loadServerBoard(): Promise<DashboardLayout | null> {
  try {
    const cfg = await fetch(DASH_CONFIG_URL);
    if (!cfg.ok) throw new Error(`config HTTP ${cfg.status}`);
    const j: unknown = await cfg.json();
    const name =
      j && typeof j === 'object' && typeof (j as { board?: unknown }).board === 'string'
        ? ((j as { board: string }).board ?? '').trim()
        : '';
    // Remember what's published so the Publish button can show drift.
    publishedName =
      j && typeof j === 'object' && typeof (j as { published?: unknown }).published === 'string'
        ? ((j as { published: string }).published ?? '').trim()
        : '';
    if (!name) { refreshPublishButton(); return null; }
    const r = await fetch(`${SBASE}/files/dashboards/${encodeURIComponent(name)}`);
    if (!r.ok) throw new Error(`board ${name}: HTTP ${r.status}`);
    const json = await r.json();
    if (isLayoutJson(json)) {
      currentBoardName = name;
      refreshPublishButton();
      return migrateLayout(json);
    }
    console.warn(`[dashboard] seed board ${name} is not a valid layout`);
    return null;
  } catch (e) {
    console.warn(`[dashboard] server board load skipped: ${(e as Error).message}`);
    return null;
  }
}

/** The two seed pointers this app manages:
 *  - `board`     — the operator's WORKING board (what this editor loads).
 *  - `published` — what the read-only client view (`?view=1`) renders.
 *  Split on purpose: without it every mid-edit save is instantly what a client
 *  sees. Publish is the operator's explicit "this state is ready" action.
 *  currentBoardName/publishedName are module state so the Publish button can
 *  reflect drift ("Publish" vs "Published ✓") without refetching. */
let currentBoardName = '';
let publishedName = '';

/** Record `name` as the seed's current board (best-effort, fire-and-forget):
 *  the next dashboard entry — any browser, any box restart — loads it back.
 *  Sends ONLY `board`; the backend preserves `published` (merge-on-put), so a
 *  routine save can never silently republish. */
function setCurrentBoard(name: string): void {
  currentBoardName = name;
  refreshPublishButton();
  void fetch(DASH_CONFIG_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ board: name }),
  })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      console.info(`[dashboard] current board → ${name}`);
    })
    .catch((e) =>
      console.warn(`[dashboard] current-board pointer update failed: ${(e as Error).message}`),
    );
}

/** Publish = point the client view at the operator's current board. */
async function publishBoard(): Promise<void> {
  if (!currentBoardName) {
    window.alert('Save the board first — Publish points the client view at a saved board file.');
    return;
  }
  try {
    const r = await fetch(DASH_CONFIG_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ board: currentBoardName, published: currentBoardName }),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    publishedName = currentBoardName;
    refreshPublishButton();
    console.info(`[dashboard] published → ${currentBoardName}`);
  } catch (e) {
    window.alert(`Publish failed: ${(e as Error).message}`);
  }
}

function refreshPublishButton(): void {
  const btn = document.getElementById('dash-publish') as HTMLButtonElement | null;
  if (!btn) return;
  const upToDate = !!currentBoardName && currentBoardName === publishedName;
  btn.textContent = upToDate ? 'Published ✓' : 'Publish to client view';
  btn.disabled = upToDate;
  btn.title = upToDate
    ? `Clients see this board (${publishedName})`
    : publishedName
      ? `Clients currently see ${publishedName}`
      : 'Clients see nothing until a board is published';
}

/**
 * Pull every mounted widget's LIVE state into `layout.cells[].state`.
 *
 * Without this, `layout` only carries the state captured at the last save or
 * cell rebuild, so anything reading the layout sees a stale board — which is
 * how the LLM agent could describe where the PMS cell was and how it was
 * configured, but not which tag was selected in it a second ago.
 *
 * Exposed as `window.dashboardSyncState()` so in-window readers can refresh on
 * demand. Cheap (a getState per cell), but not free, so it is called rather
 * than run on a timer.
 */
function syncCellState(): void {
  for (const m of mounted.values()) {
    if (m.instance?.getState) {
      try {
        m.cell.state = m.instance.getState();
      } catch (e) {
        console.warn(`[dashboard] getState failed for ${m.cell.id}: ${(e as Error).message}`);
      }
    }
  }
}

function persistLayout(): void {
  if (VIEW_MODE) return;   // a viewer changes nothing for anyone
  // Collect each widget's latest state into the layout before persisting.
  syncCellState();
  // serialiseLayout GCs unreferenced resources and orders the resources block
  // last in the JSON. Mutate `layout.resources` to the pruned set so the
  // in-memory and stored views match.
  const serialised = serialiseLayout(layout);
  layout.resources = serialised.resources;
  // Cache only on site builds: the default build has no durable store to
  // reconcile against, so it stays stateless (see header comment).
  if (SBASE) {
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(serialised));
    } catch (e) {
      console.warn(`[dashboard] localStorage write failed: ${(e as Error).message}`);
    }
  }
  (window as unknown as { dashboardLayout?: DashboardLayout }).dashboardLayout = layout;
}

// ---------------------------------------------------------------------------
// Resource API — backs DashboardCtx.addResource / getResource / removeResource.
// Resources live in `layout.resources` so they're saved as part of the layout
// JSON (glTF-style: blobs at the bottom, cells reference by `{ $ref }`).
// ---------------------------------------------------------------------------

function ensureResources(): Record<string, ResourceEntry> {
  if (!layout.resources) layout.resources = {};
  return layout.resources;
}

function addResource(entry: ResourceEntry): ResourceRef {
  const store = ensureResources();
  const id = newResourceId();
  store[id] = entry;
  return { $ref: id };
}

function getResource(ref: ResourceRef | string | null | undefined): ResourceEntry | undefined {
  const id =
    typeof ref === 'string'
      ? ref
      : ref && typeof ref === 'object' && typeof (ref as ResourceRef).$ref === 'string'
        ? (ref as ResourceRef).$ref
        : null;
  if (!id || !layout.resources) return undefined;
  return layout.resources[id];
}

function removeResource(ref: ResourceRef | string): void {
  const id = typeof ref === 'string' ? ref : ref?.$ref;
  if (!id || !layout.resources) return;
  delete layout.resources[id];
}

// The site-storage model picker every viewer cell shares — the SAME helper the
// editor's sidebar uses, so a cell lists exactly what the editor lists. Built
// once (it is stateless) and only on a site build, where a NAS exists to list.
const pickSiteAsset = createSiteAssetPicker();

/**
 * Where a widget's uploaded file goes.
 *
 * Content-addressed PUT into the app's own files prefix — `/files` standalone,
 * `${SBASE}/files` on a hub — so the same code serves both and neither has a
 * path written into it. Content-addressing means re-uploading the same clip
 * costs one HEAD instead of the bytes.
 *
 * Unlike `addResource`, nothing lands in the layout JSON: the cell keeps a URL.
 * That is the whole point for video, where base64 would put tens of megabytes
 * into every save of the board.
 */
const uploadWidgetFile = createContentAddressedUploader({
  prefix: `${SBASE}/files`,
  onStatus: (m) => console.info(`[dashboard upload] ${m}`),
});

// ---------------------------------------------------------------------------
// The cell bus — one cell reading another cell's output
// ---------------------------------------------------------------------------

/**
 * Cells announce what they are producing here; reader cells watch it.
 *
 * The address is cell → PLUGIN, not cell alone: a viewer cell installs every
 * plugin its snapshot names, so "the slope cell" is not a thing. That is also
 * how the engine already stores it (`viewer.execLog` is keyed by plugin id,
 * every `ResultEntry` carries `producer`), so the feed is a flattening, not a
 * new model.
 *
 * STICKY by construction — the map holds the last value, so a reader mounted
 * after the producer already ran shows the current number instead of waiting
 * for the next run. Only cells on the CURRENT tab can appear: the rest are not
 * mounted and have nothing live to say.
 */
const feeds = new Map<string, CellFeed>();
const feedSubs = new Set<(f: ReadonlyMap<string, CellFeed>) => void>();

function notifyFeeds(): void {
  for (const fn of feedSubs) {
    try {
      fn(feeds);
    } catch (e) {
      console.warn('[dashboard] feed subscriber threw', e);
    }
  }
}

function publishFeed(cellId: string, widgetType: string, feed: CellFeedInput | null): void {
  if (feed == null) {
    if (!feeds.delete(cellId)) return;
  } else {
    feeds.set(cellId, { cellId, widgetType, ...feed });
  }
  notifyFeeds();
}

/** Drop a disposed cell's feed — a reader must not keep rendering numbers from
 *  a cell that is gone. Called from every teardown path. */
function dropFeed(cellId: string): void {
  if (feeds.delete(cellId)) notifyFeeds();
}

function listCells(selfId: string): CellSummary[] {
  return activeTab()
    .cells.filter((c) => c.id !== selfId)
    .map((c) => {
      const f = feeds.get(c.id);
      return { id: c.id, type: c.type, title: f?.title, publishes: !!f };
    });
}

function makeCtx(cellId: string, widgetType: string): DashboardCtx {
  return {
    cellId,
    widgetType,
    addResource,
    getResource,
    removeResource,
    // Per-cell result picker — only on base-portable builds (SBASE set). When
    // absent, snapshot cells keep only drop / click-to-browse-local (unchanged).
    ...(SBASE ? { pickResultUrl: pickCheckpointUrl, pickAssetUrl: pickSiteAsset } : {}),
    // Uploading works on BOTH builds — `/files` takes a WebDAV PUT standalone
    // exactly as the site prefix does — so this one is not behind the seam.
    uploadFile: (file: File) => uploadWidgetFile(file),
    // The cell bus (see above). `subscribe` fires immediately so a reader
    // renders the current value on mount rather than on the next change.
    publish: (feed) => publishFeed(cellId, widgetType, feed),
    subscribe: (fn) => {
      feedSubs.add(fn);
      fn(feeds);
      return () => {
        feedSubs.delete(fn);
      };
    },
    cells: () => listCells(cellId),
  };
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

/**
 * Resolve the grid for the CURRENT viewport and push it to CSS.
 *
 * The board stores a cell resolution, not a column count (see layout.ts), so a
 * 4K wall genuinely shows more cells while a laptop shows the same composition
 * at a smaller cell. Cells are re-placed afterwards because a narrower grid
 * clamps rects that were authored wider.
 */
/** The rail's share of the window: 7%, clamped so it never becomes a sliver on
 *  a laptop or a corridor on a wall. Written to --rail-w, which both the rail
 *  and its edge handle read. */
function applyRailWidth(): void {
  const w = document.body.clientWidth || window.innerWidth || 1920;
  const px = Math.round(Math.min(220, Math.max(132, w * 0.07)));
  // Write the OPEN width, not --rail-w: an inline --rail-w would outrank the
  // `body.tabs-collapsed { --rail-w: 0 }` rule and the drawer would stop
  // giving its space back when shut.
  document.body.style.setProperty('--rail-open-w', `${px}px`);
}

function applyGridSize(): void {
  applyRailWidth();
  // Measure the stage; the grid fills it, so this can't feed back on itself.
  const slot = grid.parentElement ?? grid;
  const slotRect = slot.getBoundingClientRect();
  const style = getComputedStyle(grid);
  const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
  const padY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
  gridGeom = computeGrid(slotRect.width - padX, slotRect.height - padY, layout.grid);
  grid.style.setProperty('--cols', String(gridGeom.cols));
  grid.style.setProperty('--rows', String(gridGeom.rows));
  grid.style.setProperty('--gap', `${gridGeom.gap}px`);
  // Drag steps and the edit-mode backdrop: the two axes have different pitches
  // now that cells stretch to fill rather than staying square.
  grid.style.setProperty('--pitch-x', `${gridGeom.colW + gridGeom.gap}px`);
  grid.style.setProperty('--pitch-y', `${gridGeom.rowH + gridGeom.gap}px`);
  for (const m of mounted.values()) placeCell(m.root, m.cell);
}

/** Write a cell's rect to the DOM, clamped into the live grid (render-time
 *  only — the stored rect keeps the size it was authored at). */
function placeCell(root: HTMLElement, cell: DashboardCell): void {
  const r = clampRect(cell.rect, gridGeom.cols, gridGeom.rows);
  root.style.gridColumn = `${r.x + 1} / span ${r.w}`;
  root.style.gridRow = `${r.y + 1} / span ${r.h}`;
}

/** The header line reads the board's own title (`layout.title`), 'Dashboard'
 *  when unset. The tab title follows so a wall of browser tabs stays legible. */
function renderHeader(): void {
  const h1 = document.querySelector<HTMLHeadingElement>('#dash-header h1');
  if (!h1) return;
  const title = layout.title?.trim() || 'Dashboard';
  h1.textContent = title;
  document.title = title;
}

function renderLayout(): void {
  renderHeader();
  // Capture every live widget's state BEFORE tearing the instances down: a
  // rebuild restores cells via applyState(cell.state), and any caller that
  // forgot to sync first handed the new instances STALE state — adding a cell
  // blanked a sibling viewer's just-picked scene (and withdrew its cell-bus
  // feed with it, which is how the bug was found). One capture here covers
  // every caller, including future ones; for callers that discard the layout
  // afterwards (clear / load-file) the extra sync writes into an object that
  // is about to be dropped — harmless.
  syncCellState();
  tearDownAll();
  // A rebuild drops every cell element, so the maximise can't survive it.
  maximisedCellId = null;
  grid.classList.remove('has-max');
  grid.innerHTML = '';
  for (const cell of activeTab().cells) {
    mountCell(cell);
  }
  renderTabRail();
  setTabsOpen(tabsOpen(), false);
  applyGridSize();
  syncConfigDrawer();
}

/**
 * The left tab rail. A board is a stack of independent grids — a CCTV wall, an
 * overview for the boss — and the rail is how you move between them. Tabs are
 * created, renamed and removed here; the "+" sits at the bottom, below the
 * list, so it doesn't move as tabs are added.
 *
 * Rename/remove/hide/reorder are edit-mode only: in view mode the rail is just
 * navigation, so a wall-mounted board can't be re-authored by a passer-by.
 */
function renderTabRail(): void {
  const rail = document.getElementById('dash-tabs');
  if (!rail) return;
  rail.replaceChildren();

  for (const tab of layout.tabs) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tab-btn' + (tab.id === layout.activeTabId ? ' active' : '');
    btn.dataset.tabId = tab.id;
    if (document.body.classList.contains('edit-mode')) {
      btn.title = 'Drag to reorder · double-click to rename';
    }

    const name = document.createElement('span');
    name.className = 'tab-name';
    name.textContent = tab.name;
    btn.appendChild(name);
    if (tab.viewerHidden) {
      const flag = document.createElement('span');
      flag.className = 'tab-flag';
      flag.textContent = 'operator';
      flag.title = 'Hidden from the client view';
      btn.appendChild(flag);
    }
    const count = document.createElement('span');
    count.className = 'tab-count';
    count.textContent = String(tab.cells.length);
    btn.appendChild(count);

    btn.addEventListener('click', () => {
      // The click that ends a reorder drag is not a pick.
      if (tabClickBlocked) return;
      selectTab(tab.id);
    });
    btn.addEventListener('pointerdown', (e) => beginTabDrag(tab.id, btn, e));
    // Rename in place — double-click is the discoverable gesture, and it only
    // arms in edit mode.
    btn.addEventListener('dblclick', () => {
      if (!document.body.classList.contains('edit-mode')) return;
      const next = window.prompt('Tab name', tab.name);
      if (next == null) return;
      const trimmed = next.trim();
      if (!trimmed) return;
      tab.name = trimmed;
      renderTabRail();
      persistLayout();
    });

    const tools = document.createElement('span');
    tools.className = 'tab-tools';
    const hide = document.createElement('button');
    hide.type = 'button';
    hide.className = 'tab-tool';
    hide.textContent = tab.viewerHidden ? '🙈' : '👁';
    hide.title = tab.viewerHidden ? 'Hidden from the client view — click to show' : 'Visible to the client view — click to hide';
    hide.addEventListener('click', (e) => {
      e.stopPropagation();
      if (tab.viewerHidden) delete tab.viewerHidden;
      else tab.viewerHidden = true;
      renderTabRail();
      persistLayout();
    });
    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'tab-tool danger';
    del.textContent = '✕';
    del.title = 'Remove this tab';
    del.addEventListener('click', (e) => {
      e.stopPropagation();
      removeTab(tab.id);
    });
    tools.append(hide, del);
    btn.appendChild(tools);

    rail.appendChild(btn);
  }

  const add = document.createElement('button');
  add.type = 'button';
  add.id = 'dash-add-tab';
  add.className = 'tab-add';
  add.textContent = '+';
  add.title = 'Add a tab';
  add.addEventListener('click', () => addTab());
  rail.appendChild(add);
}

/**
 * Open/close the tab drawer. Collapsing hands the rail's width back to the
 * board, which re-resolves through the ResizeObserver on #dash-grid: the cells
 * grow, and past the 16-column floor (~2040 px of board) the extra space
 * becomes an extra COLUMN rather than wider cells.
 */
function setTabsOpen(open: boolean, persist = true): void {
  document.body.classList.toggle('tabs-collapsed', !open);
  const handle = document.getElementById('dash-tabs-handle');
  if (handle) {
    handle.textContent = open ? '‹' : '›';
    handle.title = open ? 'Hide the tab rail' : 'Show the tab rail';
  }
  layout.ui = { ...(layout.ui ?? {}), tabsOpen: open };
  if (persist) persistLayout();
}

function tabsOpen(): boolean {
  return layout.ui?.tabsOpen !== false; // absent = open, the state boards ship in
}

function selectTab(id: string): void {
  if (id === layout.activeTabId) return;
  if (!layout.tabs.some((t) => t.id === id)) return;
  setMaximisedCell(null);
  // Flush the outgoing tab's widget state — it is saved from `cell.state`,
  // and a parked cell is no longer synced.
  syncCellState();
  parkActiveTab();
  layout.activeTabId = id;
  const back = parked.get(id);
  if (back) {
    // Returning: show the kept cells as they were — no reload.
    parked.delete(id);
    for (const m of back) {
      mounted.set(m.cell.id, m);
      m.root.style.display = '';
      tellVisible(m, true);
    }
    // A viewer hidden through a window resize measured 0×0; let every
    // viewer re-measure now that it is on screen again.
    window.dispatchEvent(new Event('resize'));
  } else {
    for (const cell of activeTab().cells) mountCell(cell);
  }
  renderTabRail();
  setTabsOpen(tabsOpen(), false);
  applyGridSize();
  syncConfigDrawer();
  persistLayout();
}

function addTab(): void {
  const tab = newTab(`Tab ${layout.tabs.length + 1}`);
  layout.tabs.push(tab);
  selectTab(tab.id);
  renderTabRail();
  persistLayout();
}

function removeTab(id: string): void {
  const tab = layout.tabs.find((t) => t.id === id);
  if (!tab) return;
  if (layout.tabs.length === 1) {
    window.alert('A board needs at least one tab.');
    return;
  }
  const ok = window.confirm(
    tab.cells.length
      ? `Remove tab "${tab.name}" and its ${tab.cells.length} widget(s)?`
      : `Remove tab "${tab.name}"?`,
  );
  if (!ok) return;
  const wasActive = layout.activeTabId === id;
  layout.tabs = layout.tabs.filter((t) => t.id !== id);
  if (wasActive) layout.activeTabId = layout.tabs[0].id;
  renderLayout();
  persistLayout();
}

// ---------------------------------------------------------------------------
// Tab order (edit mode): drag a tab up or down the rail. A mouse drags at once;
// a finger holds the tab for a moment first, so a plain swipe still scrolls a
// long rail. The rail reorders live and the board takes the order on release;
// a cancel redraws the rail from the board, so nothing half-done is kept.
// ---------------------------------------------------------------------------

interface TabDrag {
  id: string;
  btn: HTMLElement;
  pointerId: number;
  startX: number;
  startY: number;
  /** A mouse or pen is armed at once, a finger after TAB_HOLD_MS. */
  armed: boolean;
  /** Past the threshold: the rail is being reordered. */
  moving: boolean;
  timer: number;
}

let tabDrag: TabDrag | null = null;
/** Swallows the one click the browser sends with a drag's release. */
let tabClickBlocked = false;
const TAB_DRAG_PX = 5;
const TAB_HOLD_MS = 350;
/** A finger that moves this far before the hold ends is scrolling. */
const TAB_TOUCH_SLOP_PX = 8;

function beginTabDrag(id: string, btn: HTMLElement, ev: PointerEvent): void {
  if (!document.body.classList.contains('edit-mode')) return;
  if (tabDrag || !ev.isPrimary || ev.button !== 0) return;
  // The eye and the ✕ keep their own clicks.
  if ((ev.target as Element | null)?.closest('.tab-tool')) return;
  const touch = ev.pointerType === 'touch';
  const d: TabDrag = {
    id, btn, pointerId: ev.pointerId, startX: ev.clientX, startY: ev.clientY,
    armed: !touch, moving: false, timer: 0,
  };
  if (touch) {
    d.timer = window.setTimeout(() => {
      if (tabDrag !== d) return;
      d.armed = true;
      btn.classList.add('tab-dragging');
    }, TAB_HOLD_MS);
  }
  tabDrag = d;
  window.addEventListener('pointermove', onTabDragMove);
  window.addEventListener('pointerup', onTabDragUp);
  window.addEventListener('pointercancel', onTabDragCancel);
}

function onTabDragMove(ev: PointerEvent): void {
  const d = tabDrag;
  if (!d || ev.pointerId !== d.pointerId) return;
  const dist = Math.hypot(ev.clientX - d.startX, ev.clientY - d.startY);
  if (!d.armed) {
    // Moved before the hold ended: a scroll, not a drag.
    if (dist > TAB_TOUCH_SLOP_PX) endTabDrag(false);
    return;
  }
  const rail = d.btn.parentElement;
  if (!rail) { endTabDrag(false); return; }
  if (!d.moving) {
    if (dist < TAB_DRAG_PX) return;
    d.moving = true;
    d.btn.classList.add('tab-dragging');
    try { d.btn.setPointerCapture(d.pointerId); } catch { /* pointer already gone */ }
  }
  ev.preventDefault();
  // Slot the tab above the first other tab whose middle is below the pointer.
  // Measuring the OTHER tabs keeps it steady: moving the dragged tab shifts
  // its neighbour by one tab height, which never crosses the pointer back.
  let before: Element | null = rail.querySelector('.tab-add');
  for (const b of rail.querySelectorAll<HTMLElement>('.tab-btn')) {
    if (b === d.btn) continue;
    const r = b.getBoundingClientRect();
    if (ev.clientY < r.top + r.height / 2) { before = b; break; }
  }
  if (d.btn.nextElementSibling !== before) rail.insertBefore(d.btn, before);
  // Near either end, scroll a long rail along.
  const rr = rail.getBoundingClientRect();
  if (ev.clientY < rr.top + 24) rail.scrollTop -= 12;
  else if (ev.clientY > rr.bottom - 24) rail.scrollTop += 12;
}

function onTabDragUp(ev: PointerEvent): void {
  if (tabDrag && ev.pointerId === tabDrag.pointerId) endTabDrag(true);
}

function onTabDragCancel(ev: PointerEvent): void {
  if (tabDrag && ev.pointerId === tabDrag.pointerId) endTabDrag(false);
}

/** Close a tab drag. `commit` hands the rail's order to the board; otherwise
 *  the rail is redrawn from the board, which undoes the live preview. */
function endTabDrag(commit: boolean): void {
  const d = tabDrag;
  tabDrag = null;
  window.removeEventListener('pointermove', onTabDragMove);
  window.removeEventListener('pointerup', onTabDragUp);
  window.removeEventListener('pointercancel', onTabDragCancel);
  if (!d) return;
  window.clearTimeout(d.timer);
  d.btn.classList.remove('tab-dragging');
  if (d.btn.hasPointerCapture(d.pointerId)) d.btn.releasePointerCapture(d.pointerId);
  if (!d.moving) return; // a tap, or a hold that never moved: the click picks as usual
  // A mouse release dispatches its click in the same task, so clearing on the
  // next task blocks that click and no later one.
  tabClickBlocked = true;
  window.setTimeout(() => { tabClickBlocked = false; }, 0);
  const order = [...(d.btn.parentElement?.querySelectorAll<HTMLElement>('.tab-btn') ?? [])]
    .map((b) => b.dataset.tabId);
  const from = layout.tabs.findIndex((t) => t.id === d.id);
  const to = order.indexOf(d.id);
  const moved = commit && from >= 0 && to >= 0 && to !== from;
  if (moved) layout.tabs = moveTab(layout.tabs, from, to);
  renderTabRail();
  if (moved) persistLayout();
}

// ---------------------------------------------------------------------------
// Drag to move · corner-drag to resize (edit mode)
//
// Both snap to whole cells and REFUSE overlap rather than reflowing the board:
// an operator who spent ten minutes arranging a wall should never have a
// widget shove its neighbours aside. An illegal drop simply snaps back.
// ---------------------------------------------------------------------------

interface DragState {
  cell: DashboardCell;
  root: HTMLElement;
  handle: HTMLElement;
  pointerId: number;
  mode: 'move' | 'resize';
  startX: number;
  startY: number;
  origin: CellRect;
  preview: CellRect;
}

let drag: DragState | null = null;
let dragGhost: HTMLElement | null = null;
const extraTouchEvents = ['pointerdown', 'pointermove', 'pointerup', 'pointercancel'] as const;

function beginDrag(cell: DashboardCell, root: HTMLElement, mode: 'move' | 'resize', ev: PointerEvent): void {
  if (!document.body.classList.contains('edit-mode')) return;
  ev.preventDefault();
  ev.stopPropagation();
  // Only the pointer that started the gesture owns it. A second finger on
  // either handle must not replace the origin or create a second ghost.
  if (drag || !ev.isPrimary) return;
  const handle = ev.currentTarget as HTMLElement;
  drag = {
    cell, root, handle, pointerId: ev.pointerId, mode,
    startX: ev.clientX, startY: ev.clientY,
    origin: { ...cell.rect },
    preview: { ...cell.rect },
  };
  root.classList.add('dragging');
  dragGhost = document.createElement('div');
  dragGhost.className = 'cell-ghost';
  grid.appendChild(dragGhost);
  paintGhost();
  handle.setPointerCapture(ev.pointerId);
  for (const type of extraTouchEvents) window.addEventListener(type, blockAdditionalTouch, true);
  window.addEventListener('pointermove', onDragMove);
  window.addEventListener('pointerup', endDrag);
  window.addEventListener('pointercancel', cancelDrag);
  handle.addEventListener('lostpointercapture', cancelDrag);
}

function paintGhost(): void {
  if (!dragGhost || !drag) return;
  const r = drag.preview;
  dragGhost.style.gridColumn = `${r.x + 1} / span ${r.w}`;
  dragGhost.style.gridRow = `${r.y + 1} / span ${r.h}`;
  const legal = rectIsFree(activeTab(), r, drag.cell.id);
  dragGhost.classList.toggle('illegal', !legal);
}

function blockAdditionalTouch(ev: PointerEvent): void {
  if (!drag || ev.pointerType !== 'touch' || ev.pointerId === drag.pointerId) return;
  // Keep a second contact from starting OrbitControls or pressing another
  // cell's chrome while the first finger owns an edit gesture.
  ev.preventDefault();
  ev.stopPropagation();
}

function onDragMove(ev: PointerEvent): void {
  if (!drag || ev.pointerId !== drag.pointerId) return;
  ev.preventDefault();
  ev.stopPropagation();
  const dx = Math.round((ev.clientX - drag.startX) / (gridGeom.colW + gridGeom.gap));
  const dy = Math.round((ev.clientY - drag.startY) / (gridGeom.rowH + gridGeom.gap));
  const o = drag.origin;
  const next: CellRect = drag.mode === 'move'
    ? { x: o.x + dx, y: o.y + dy, w: o.w, h: o.h }
    : { x: o.x, y: o.y, w: o.w + dx, h: o.h + dy };
  // Keep the preview inside the grid at all times — a widget dragged off the
  // edge should stop at the edge, not vanish.
  next.w = Math.max(1, Math.min(next.w, gridGeom.cols));
  next.h = Math.max(1, Math.min(next.h, gridGeom.rows));
  next.x = Math.max(0, Math.min(next.x, gridGeom.cols - next.w));
  next.y = Math.max(0, Math.min(next.y, gridGeom.rows - next.h));
  drag.preview = next;
  paintGhost();
}

function endDrag(ev: PointerEvent): void {
  if (!drag || ev.pointerId !== drag.pointerId) return;
  ev.preventDefault();
  ev.stopPropagation();
  finishDrag(true);
}

function cancelDrag(ev: PointerEvent): void {
  if (drag && ev.pointerId === drag.pointerId) finishDrag(false);
}

/** Only an owner's pointerup commits. Cancellation or a detached handle
 *  abandons the preview, including when the tab or Edit mode changes. */
function finishDrag(commit: boolean): void {
  for (const type of extraTouchEvents) window.removeEventListener(type, blockAdditionalTouch, true);
  window.removeEventListener('pointermove', onDragMove);
  window.removeEventListener('pointerup', endDrag);
  window.removeEventListener('pointercancel', cancelDrag);
  const d = drag;
  drag = null;
  dragGhost?.remove();
  dragGhost = null;
  if (!d) return;
  d.handle.removeEventListener('lostpointercapture', cancelDrag);
  if (d.handle.hasPointerCapture(d.pointerId)) d.handle.releasePointerCapture(d.pointerId);
  d.root.classList.remove('dragging');
  if (!commit) return;
  const moved = d.preview.x !== d.origin.x || d.preview.y !== d.origin.y
    || d.preview.w !== d.origin.w || d.preview.h !== d.origin.h;
  if (!moved) return;
  if (!rectIsFree(activeTab(), d.preview, d.cell.id)) {
    // Snap back rather than displace whatever is already there.
    placeCell(d.root, d.cell);
    return;
  }
  d.cell.rect = d.preview;
  placeCell(d.root, d.cell);
  persistLayout();
}

/**
 * Which cell currently fills the board, if any.
 *
 * One at a time, deliberately: the viewer cell's chrome (sidebar, toolbar,
 * info history) is a set of viewport-fixed singletons in viewer-ui, so exactly
 * one cell can wear them — and it should be the one with room for them.
 */
let maximisedCellId: string | null = null;

function setMaximisedCell(id: string | null): void {
  if (maximisedCellId === id) return;
  const previous = maximisedCellId;
  maximisedCellId = id;
  grid.classList.toggle('has-max', id !== null);
  for (const m of mounted.values()) {
    const on = m.cell.id === id;
    m.root.classList.toggle('maximised', on);
    if (m.cell.id === id || m.cell.id === previous) {
      // Tell the widget, so a viewer cell can raise or hide its bars. Optional
      // by contract — a chart cell simply doesn't implement it.
      const inst = m.instance as { setMaximised?: (on: boolean) => void } | null;
      try { inst?.setMaximised?.(on); } catch (e) { console.warn(`[dashboard] setMaximised: ${(e as Error).message}`); }
    }
  }
  console.info(`[dashboard] ${id ? `cell ${id} maximised — Esc restores` : 'cell restored'}`);
}

// Escape leaves a maximised cell. Registered once, and only acts when a cell
// is up, so it never swallows Escape from a widget's own UI. An Esc that just
// closed a showing info card (viewer.ui.popup marks it) is the card's: the
// first Esc closes the card, the next one restores the cell.
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && maximisedCellId && !Popup.tookEscape(e)) setMaximisedCell(null);
});

function tearDownAll(): void {
  finishDrag(false);
  for (const cells of parked.values()) disposeCells(cells);
  parked.clear();
  for (const m of mounted.values()) {
    try {
      m.instance?.dispose();
    } catch (e) {
      console.error(e);
    }
    dropFeed(m.cell.id);
  }
  mounted.clear();
}

function mountCell(cell: DashboardCell): void {
  const root = document.createElement('div');
  root.className = 'cell';
  root.dataset.cellId = cell.id;

  placeCell(root, cell);

  const hostEl = document.createElement('div');
  hostEl.className = 'cell-host';
  root.appendChild(hostEl);

  // Edit-mode overlay (hidden via CSS unless body.edit-mode).
  root.appendChild(buildEditOverlay(cell));

  // Maximise: the cell fills the board. Available in VIEW mode too — this is
  // how an operator gets a working editor out of a wall board, not an
  // authoring nicety. Escape restores.
  const max = document.createElement('button');
  max.type = 'button';
  max.className = 'cell-max';
  max.title = 'Maximise this cell (Esc to restore)';
  max.textContent = '⤢';
  max.addEventListener('click', (ev) => {
    ev.stopPropagation();
    setMaximisedCell(maximisedCellId === cell.id ? null : cell.id);
  });
  root.appendChild(max);

  // Bottom-right resize grip (edit mode only, via CSS).
  const grip = document.createElement('div');
  grip.className = 'cell-resize';
  grip.title = 'Drag to resize';
  grip.addEventListener('pointerdown', (ev) => beginDrag(cell, root, 'resize', ev));
  root.appendChild(grip);

  grid.appendChild(root);

  const widget = host.get(cell.type);
  const record: MountedCell = { cell, instance: null, root, hostEl };
  mounted.set(cell.id, record);

  if (!widget) {
    renderUnknownPlaceholder(hostEl, cell.type);
    return;
  }

  const ctx: DashboardCtx = makeCtx(cell.id, cell.type);

  try {
    const instance = widget.mount(hostEl, cell.config, ctx);
    record.instance = instance;
    if (cell.state != null && instance.applyState) {
      void Promise.resolve(instance.applyState(cell.state)).catch((e) =>
        console.warn(`[dashboard] applyState failed for ${cell.id}: ${(e as Error).message}`),
      );
    }
  } catch (e) {
    console.error(`[dashboard] mount failed for ${cell.id} (${cell.type})`, e);
    renderUnknownPlaceholder(hostEl, cell.type, (e as Error).message);
  }
}

function renderUnknownPlaceholder(hostEl: HTMLElement, type: string, detail?: string): void {
  hostEl.innerHTML = '';
  const ph = document.createElement('div');
  ph.className = 'cell-unknown';
  const title = document.createElement('div');
  title.className = 'cell-unknown-title';
  title.textContent = `Unknown widget type: ${type}`;
  ph.appendChild(title);
  const hint = document.createElement('div');
  hint.className = 'cell-unknown-hint';
  hint.textContent = detail ?? 'Open in Edit mode to change';
  ph.appendChild(hint);
  hostEl.appendChild(ph);
}

function rebuildCell(cell: DashboardCell): void {
  if (drag?.cell === cell) finishDrag(false);
  const m = mounted.get(cell.id);
  if (!m) return;
  // Capture live state BEFORE dispose so the rebuild restores it via applyState.
  // Without this, a config change (Apply) would wipe out the widget's runtime
  // view (e.g. map pan/zoom) because the new instance has no state to restore.
  if (m.instance?.getState) {
    try {
      m.cell.state = m.instance.getState();
    } catch (e) {
      console.warn(`[dashboard] getState failed for ${m.cell.id}: ${(e as Error).message}`);
    }
  }
  try {
    m.instance?.dispose();
  } catch (e) {
    console.error(e);
  }
  m.root.remove();
  mounted.delete(cell.id);
  dropFeed(cell.id);
  mountCell(cell);
  // mountCell appends to the grid; reorder so layout.cells order is preserved.
  reorderCells();
  syncConfigDrawer();
  persistLayout();
}

function reorderCells(): void {
  for (const cell of activeTab().cells) {
    const m = mounted.get(cell.id);
    if (m) grid.appendChild(m.root);
  }
}

/**
 * Re-mount cells that rendered as "Unknown" at initial paint but whose
 * widget type is now registered. Called after the marketplace registry
 * resolves so that cells referencing marketplace widgets (e.g. map-shapes.v1)
 * come alive on a normal page reload, not only after dropping a layout JSON.
 *
 * No state to preserve here — these cells never successfully mounted, so
 * `cell.state` from the persisted layout is the right (and only) input.
 */
function remountNewlyKnownCells(): void {
  for (const cell of activeTab().cells) {
    const m = mounted.get(cell.id);
    if (!m || m.instance != null) continue;
    if (!host.get(cell.type)) continue;
    m.root.remove();
    mounted.delete(cell.id);
    mountCell(cell);
  }
  reorderCells();
  syncConfigDrawer();
  // Also refresh existing cells' type dropdowns: edit mode might be open,
  // and freshly-loaded marketplace widgets should appear in every cell's
  // type picker, not just the cells we just re-mounted.
  refreshAllTypeSelects();
}

function refreshAllTypeSelects(): void {
  for (const m of mounted.values()) {
    const sel = m.root.querySelector('.cell-edit-type');
    if (sel instanceof HTMLSelectElement) refillTypeSelect(sel, m.cell.type);
  }
}

// ---------------------------------------------------------------------------
// Edit-mode overlay
// ---------------------------------------------------------------------------

function buildEditOverlay(cell: DashboardCell): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'cell-edit-overlay';

  const label = document.createElement('div');
  label.className = 'cell-edit-label';
  label.textContent = cell.id;
  label.title = 'Drag to move';
  // The label IS the grab bar — a separate handle would eat space in small
  // cells, and the label is already the one strip that is never the widget.
  label.addEventListener('pointerdown', (ev) => {
    const root = label.closest('.cell') as HTMLElement | null;
    if (root) beginDrag(cell, root, 'move', ev);
  });
  overlay.appendChild(label);

  const typeSel = document.createElement('select');
  typeSel.className = 'cell-edit-type';
  refillTypeSelect(typeSel, cell.type);
  typeSel.addEventListener('change', () => {
    cell.type = typeSel.value;
    cell.config = {};
    cell.state = undefined;
    rebuildCell(cell);
  });
  overlay.appendChild(typeSel);

  // Per-cell client visibility. Lives on the CELL (not the widget config) so
  // the /view/ renderer can filter without knowing any widget's config shape.
  // Mutating the cell is enough — every save path serialises layout.cells.
  const visRow = document.createElement('label');
  visRow.className = 'cell-edit-vis';
  visRow.style.cssText =
    'display:flex;align-items:center;gap:6px;font-size:11px;color:#c7d0d9;cursor:pointer';
  const visCb = document.createElement('input');
  visCb.type = 'checkbox';
  visCb.checked = cell.viewerHidden === true;
  visCb.addEventListener('change', () => {
    if (visCb.checked) cell.viewerHidden = true;
    else delete cell.viewerHidden; // absent = visible; keeps board JSON lean
    label.textContent = cell.viewerHidden ? `${cell.id} · hidden from client` : cell.id;
  });
  visRow.appendChild(visCb);
  visRow.appendChild(document.createTextNode('hide from client view'));
  overlay.appendChild(visRow);
  if (cell.viewerHidden) label.textContent = `${cell.id} · hidden from client`;

  const actions = document.createElement('div');
  actions.className = 'cell-edit-actions';

  const configureBtn = document.createElement('button');
  configureBtn.className = 'cell-edit-btn';
  configureBtn.textContent = 'Configure';
  configureBtn.addEventListener('click', () => openConfigDrawer(cell));
  actions.appendChild(configureBtn);

  const dupBtn = document.createElement('button');
  dupBtn.className = 'cell-edit-btn';
  dupBtn.textContent = 'Duplicate';
  dupBtn.addEventListener('click', () => duplicateCell(cell));
  actions.appendChild(dupBtn);

  const rmBtn = document.createElement('button');
  rmBtn.className = 'cell-edit-btn cell-edit-btn-danger';
  rmBtn.textContent = 'Remove';
  rmBtn.addEventListener('click', () => removeCell(cell));
  actions.appendChild(rmBtn);

  overlay.appendChild(actions);
  return overlay;
}

function refillTypeSelect(sel: HTMLSelectElement, current: string): void {
  sel.innerHTML = '';
  const types: DashboardWidget[] = host.list();
  const ids = new Set(types.map((t) => t.id));
  // Preserve a missing type in the dropdown so the user can see what's there.
  if (!ids.has(current)) {
    const o = document.createElement('option');
    o.value = current;
    o.textContent = `${current} (missing)`;
    sel.appendChild(o);
  }
  // Group by source so users can tell built-in widgets from marketplace
  // installs at a glance. <optgroup> renders a non-selectable header in
  // every browser's native select.
  const builtins: DashboardWidget[] = [];
  const market: DashboardWidget[] = [];
  for (const t of types) {
    (host.getSource(t.id) === 'marketplace' ? market : builtins).push(t);
  }
  const addOpt = (parent: HTMLElement, t: DashboardWidget) => {
    const o = document.createElement('option');
    o.value = t.id;
    o.textContent = t.label ?? t.id;
    parent.appendChild(o);
  };
  if (builtins.length > 0) {
    const g = document.createElement('optgroup');
    g.label = 'Built-in';
    for (const t of builtins) addOpt(g, t);
    sel.appendChild(g);
  }
  if (market.length > 0) {
    const g = document.createElement('optgroup');
    g.label = 'Marketplace';
    for (const t of market) addOpt(g, t);
    sel.appendChild(g);
  }
  sel.value = current;
}

function duplicateCell(cell: DashboardCell): void {
  const tab = activeTab();
  const idx = tab.cells.indexOf(cell);
  if (idx < 0) return;
  // Same size, first free slot — a copy that landed on top of its original
  // would look like nothing happened. Grows the board if it has to.
  const rect = placeOrGrow(tab, cell.rect.w, cell.rect.h);
  if (!rect) {
    window.alert(`"${tab.name}" has reached ${MAX_ROWS} rows — make room or use another tab.`);
    return;
  }
  const clone: DashboardCell = {
    id: newCellId(),
    type: cell.type,
    rect,
    config: JSON.parse(JSON.stringify(cell.config ?? {})),
  };
  tab.cells.splice(idx + 1, 0, clone);
  renderLayout();
  persistLayout();
}

function removeCell(cell: DashboardCell): void {
  if (drag?.cell === cell) finishDrag(false);
  const tab = activeTab();
  const idx = tab.cells.indexOf(cell);
  if (idx < 0) return;
  const m = mounted.get(cell.id);
  if (m) {
    try {
      m.instance?.dispose();
    } catch (e) {
      console.error(e);
    }
    m.root.remove();
    mounted.delete(cell.id);
    dropFeed(cell.id);
  }
  tab.cells.splice(idx, 1);
  syncConfigDrawer(); // closes the drawer if it was this cell's
  persistLayout();
}

/** Default size for a new widget — big enough to be useful, small enough to
 *  drop onto a busy tab. */
const NEW_CELL_SIZE = { w: 4, h: 4 };

/** Rows the board will grow to before it says no. A board past this is a
 *  scrolling document, not a dashboard. */
const MAX_ROWS = 40;

/**
 * Find room for a `w`×`h` widget, GROWING the board if there is none.
 *
 * Adding a widget must always work — that is how the dashboard behaved when it
 * was a 3 × 2 and a seventh cell simply made everyone shorter. The grid fills
 * the window, so extra rows don't add a scrollbar; they re-divide the same
 * space, exactly as adding a row to the old grid did.
 */
function placeOrGrow(tab: DashboardTab, w: number, h: number): CellRect | null {
  const found = findFreeRect(tab, w, h, layout.grid.minCols, layout.grid.minRows);
  if (found) return found;
  const grown = Math.min(MAX_ROWS, layout.grid.minRows + h);
  if (grown === layout.grid.minRows) return null; // at the ceiling
  layout.grid.minRows = grown;
  applyGridSize();
  syncToolbarValues();
  return findFreeRect(tab, w, h, layout.grid.minCols, layout.grid.minRows)
    ?? { x: 0, y: Math.max(0, grown - h), w, h };
}

function addCell(): void {
  const tab = activeTab();
  const rect = placeOrGrow(tab, NEW_CELL_SIZE.w, NEW_CELL_SIZE.h);
  if (!rect) {
    window.alert(`"${tab.name}" has reached ${MAX_ROWS} rows — remove a widget, shrink one, or add a tab.`);
    return;
  }
  const cell: DashboardCell = { id: newCellId(), type: 'viewer-snapshot', rect, config: {} };
  tab.cells.push(cell);
  renderLayout();
  persistLayout();
}

// ---------------------------------------------------------------------------
// Config drawer
// ---------------------------------------------------------------------------

/** The cell the drawer is showing, and the widget instance its form was built
 *  against. A form holds closures over that instance, so a re-mounted cell
 *  needs a fresh form, and a cell that is gone needs no drawer at all. */
let configCell: DashboardCell | null = null;
let configInstance: unknown = null;

function openConfigDrawer(cell: DashboardCell): void {
  configCell = cell;
  configInstance = mounted.get(cell.id)?.instance ?? null;
  markConfigTarget();
  drawer.innerHTML = '';
  drawer.hidden = false;
  drawer.classList.add('open');

  const head = document.createElement('div');
  head.className = 'config-drawer-head';
  const title = document.createElement('div');
  title.className = 'config-drawer-title';
  title.textContent = `Configure · ${cell.id}`;
  head.appendChild(title);
  const closeBtn = document.createElement('button');
  closeBtn.className = 'cell-edit-btn';
  closeBtn.textContent = 'Close';
  closeBtn.addEventListener('click', closeConfigDrawer);
  head.appendChild(closeBtn);
  drawer.appendChild(head);

  const body = document.createElement('div');
  body.className = 'config-drawer-body';
  drawer.appendChild(body);

  const meta = document.createElement('div');
  meta.className = 'config-drawer-meta';
  meta.textContent = `type: ${cell.type}`;
  body.appendChild(meta);

  const m = mounted.get(cell.id);
  const instance = m?.instance;
  if (instance?.renderConfigUI) {
    instance.renderConfigUI(body, (newCfg) => {
      cell.config = newCfg;
      // rebuildCell re-renders the drawer against the new instance, so the
      // form's closures (e.g. a map widget's Apply reading this.map) target
      // the live widget rather than the disposed one.
      rebuildCell(cell);
    });
  } else {
    const fallback = document.createElement('div');
    fallback.className = 'config-drawer-empty';
    fallback.textContent = 'This widget exposes no config form.';
    body.appendChild(fallback);
  }
}

function closeConfigDrawer(): void {
  configCell = null;
  configInstance = null;
  markConfigTarget();
  drawer.classList.remove('open');
  drawer.hidden = true;
  drawer.innerHTML = '';
}

/** Keep the drawer true to the board after cells change: close it when its
 *  cell was removed, is on another tab or a new board was loaded; rebuild the
 *  form when the cell was re-mounted (a new widget instance). */
function syncConfigDrawer(): void {
  if (!configCell) return;
  const cell = configCell;
  if (!document.body.classList.contains('edit-mode') || !activeTab().cells.includes(cell)) {
    closeConfigDrawer();
    return;
  }
  const instance = mounted.get(cell.id)?.instance ?? null;
  if (instance !== configInstance) openConfigDrawer(cell);
  else markConfigTarget();
}

/** Outline the cell the drawer belongs to, so it is clear which one it edits. */
function markConfigTarget(): void {
  for (const m of mounted.values()) {
    m.root.classList.toggle('config-target', configCell !== null && m.cell.id === configCell.id);
  }
}

// While the drawer is open, working in another cell moves the drawer to it.
// Capture phase: a viewer canvas stops its own pointer events. The cell's
// action buttons act on their own (Configure opens, Remove closes, Duplicate
// keeps the drawer where it is).
grid.addEventListener('pointerdown', (e) => {
  if (!configCell || !document.body.classList.contains('edit-mode')) return;
  const target = e.target as Element | null;
  if (target?.closest('.cell-edit-actions')) return;
  const id = target?.closest<HTMLElement>('.cell')?.dataset.cellId;
  if (!id || id === configCell.id) return;
  const m = mounted.get(id);
  if (m) openConfigDrawer(m.cell);
}, true);

// ---------------------------------------------------------------------------
// Top toolbar
// ---------------------------------------------------------------------------

function wireToolbar(): void {
  const editBtn = document.getElementById('dash-edit-btn') as HTMLButtonElement | null;
  editBtn?.addEventListener('click', () => {
    const on = document.body.classList.toggle('edit-mode');
    editBtn.textContent = on ? 'Done' : 'Edit';
    if (!on) {
      finishDrag(false);
      endTabDrag(false);
      closeConfigDrawer();
    }
    renderTabRail(); // the tabs' reorder hint is edit-mode only
  });

  const addBtn = document.getElementById('dash-add-cell') as HTMLButtonElement | null;
  addBtn?.addEventListener('click', () => addCell());

  // Columns and rows, independently — the grid is just a division of the
  // window, so neither number implies the other.
  const bindGrid = (id: string, get: () => number, set: (n: number) => void): void => {
    const inp = document.getElementById(id) as HTMLInputElement | null;
    if (!inp) return;
    inp.value = String(get());
    inp.addEventListener('change', () => {
      const n = Math.min(64, Math.max(2, Math.floor(Number(inp.value) || get())));
      set(n);
      for (const tab of layout.tabs) {
        for (const c of tab.cells) c.rect = clampRect(c.rect, layout.grid.minCols, layout.grid.minRows);
      }
      inp.value = String(n);
      applyGridSize();
      syncToolbarValues();
      renderLayout();
      persistLayout();
    });
  };
  bindGrid('dash-cols', () => layout.grid.minCols, (n) => { layout.grid.minCols = n; });
  bindGrid('dash-rows', () => layout.grid.minRows, (n) => { layout.grid.minRows = n; });

  const menu = document.getElementById('dash-menu') as HTMLDetailsElement | null;
  const closeMenu = (): void => {
    if (menu) menu.open = false;
  };

  // Save / Load: on a base-portable build (SBASE set) these persist the whole
  // board as a named JSON on the site NAS (WebDAV PUT + ResultPicker load),
  // mirroring the editor's checkpoint store. Unset → today's download / file
  // picker (the fallback contract).
  const saveBtn = document.getElementById('dash-save') as HTMLButtonElement | null;
  saveBtn?.addEventListener('click', () => {
    closeMenu();
    if (SBASE) void saveBoardToNas();
    else saveLayoutToFile();
  });

  const loadBtn = document.getElementById('dash-load') as HTMLButtonElement | null;
  loadBtn?.addEventListener('click', () => {
    closeMenu();
    if (SBASE) void loadBoardFromNas();
    else pickLayoutFile();
  });

  const publishBtn = document.getElementById('dash-publish') as HTMLButtonElement | null;
  if (publishBtn && !SBASE) publishBtn.hidden = true; // no seed/client view on a bare build
  publishBtn?.addEventListener('click', () => {
    closeMenu();
    void publishBoard();
  });

  const clearBtn = document.getElementById('dash-clear') as HTMLButtonElement | null;
  clearBtn?.addEventListener('click', () => {
    closeMenu();
    const tab = activeTab();
    if (tab.cells.length === 0) return;
    const ok = window.confirm(
      `Remove all ${tab.cells.length} cells on "${tab.name}"? Other tabs and saved boards are unaffected.`,
    );
    if (!ok) return;
    tab.cells = [];
    renderLayout();
    persistLayout();
  });

  // Click-outside closes the menu.
  document.addEventListener('click', (e) => {
    if (!menu || !menu.open) return;
    if (menu.contains(e.target as Node)) return;
    menu.open = false;
  });
}

function saveLayoutToFile(): void {
  // Snapshot each widget's current state into the layout object first.
  persistLayout();
  const serialised = serialiseLayout(layout);
  const blob = new Blob([JSON.stringify(serialised, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'dashboard-layout.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function pickLayoutFile(): void {
  const inp = document.createElement('input');
  inp.type = 'file';
  inp.accept = 'application/json,.json';
  inp.addEventListener('change', async () => {
    const f = inp.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      const json = JSON.parse(text);
      if (!isLayoutJson(json)) {
        console.warn('[dashboard] picked file is not a valid layout');
        return;
      }
      replaceLayout(migrateLayout(json));
    } catch (e) {
      console.warn(`[dashboard] load layout failed: ${(e as Error).message}`);
    }
  });
  inp.click();
}

function replaceLayout(next: DashboardLayout): void {
  layout = next;
  renderLayout();
  persistLayout();
}

// ---------------------------------------------------------------------------
// Board save/load to the site NAS (base-portable builds only)
//
// Reuses the EXISTING layout format (serialiseLayout / isLayoutJson / replace-
// Layout) — the same self-contained JSON the download/file-pick path emits, so
// a board saved to NAS and one saved to disk are byte-for-byte the same shape.
// Persisted picked/configured sources ride along automatically: viewer-snapshot
// cells embed their snapshot into layout.resources, so the board JSON carries
// everything needed to restore each cell without re-fetching.
// ---------------------------------------------------------------------------

async function saveBoardToNas(): Promise<void> {
  // Snapshot each widget's live state into the layout first (same as the
  // download path), then serialise (GCs unreferenced resources).
  persistLayout();
  const serialised = serialiseLayout(layout);
  const suggested = `dashboard-${boardStamp()}`;
  const raw = window.prompt('Save dashboard board as:', suggested);
  if (raw === null) return; // cancelled
  const name = sanitizeBoardName(raw) || suggested;
  const url = `${SBASE}/files/dashboards/${name}.json`;
  try {
    const res = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(serialised, null, 2),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    console.info(`[dashboard] saved board → ${name}.json`);
    boardListing?.invalidate();
    warmBoardListing();
    // This board is now the current one: record it in the site config so the
    // next dashboard entry (any browser / after restart) loads it back.
    setCurrentBoard(`${name}.json`);
  } catch (e) {
    console.warn(`[dashboard] board PUT ${name}.json failed: ${(e as Error).message}`);
    window.alert(`Save failed: ${(e as Error).message}`);
  }
}

async function loadBoardFromNas(): Promise<void> {
  if (boardPickerOpen) return;
  boardPickerOpen = true;
  let url: string | null;
  try {
    url = await ResultPicker(`${SBASE}/files/dashboards/`, {
      title: 'Load dashboard board',
      filter: (n) => n.toLowerCase().endsWith('.json'),
      listing: boardListing,
    });
  } finally {
    boardPickerOpen = false;
  }
  if (!url) return; // cancelled
  try {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const json = await r.json();
    if (!isLayoutJson(json)) {
      console.warn(`[dashboard] ${url} is not a valid board`);
      window.alert('That file is not a valid dashboard board.');
      return;
    }
    replaceLayout(migrateLayout(json));
    // Loading a board makes it the current one — persist the choice so the
    // next entry restores it (same door as Save).
    const picked = decodeURIComponent(url.split('/').pop() ?? '');
    if (picked) setCurrentBoard(picked);
  } catch (e) {
    console.warn(`[dashboard] board load failed: ${(e as Error).message}`);
    window.alert(`Load failed: ${(e as Error).message}`);
  }
}

// ---------------------------------------------------------------------------
// Grid-level drop handler
//
// Two drop payloads can land here:
//   1. A whole-layout JSON (`isLayoutJson` true) → replace the dashboard.
//   2. A viewer-snapshot JSON dropped on a viewer-snapshot cell → the widget's
//      own drop listener handles it.
//
// Event ordering: a `drop` on a child element bubbles to the grid. If the drop
// landed inside a `.cell-host`, the widget's listener already ran on the
// capture/target phase; we just bail out. Only when the drop landed on the
// grid gap (no `.cell-host` ancestor) do we attempt the layout-replace path.
// We must NOT preventDefault unless we've decided to claim the drop, or the
// widget's own listener (which fires first as the event reaches the target)
// will be short-circuited by browsers in some configurations.
// ---------------------------------------------------------------------------

function wireGridDrop(): void {
  grid.addEventListener('dragover', (e) => {
    // Only the grid-gap drop case (whole-layout replace) needs us to call
    // preventDefault here. Drops on cell hosts are managed by the widget's
    // own dragover listener.
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.cell-host')) return;
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  });

  grid.addEventListener('drop', (e) => {
    if (VIEW_MODE) { e.preventDefault(); return; }
    const target = e.target as HTMLElement | null;
    // If the drop landed inside a widget's mount target, that widget already
    // handled the event during the target/bubble phase — leave it alone.
    if (target?.closest('.cell-host')) return;

    const file = e.dataTransfer?.files?.[0];
    if (!file) return;

    // Peek at the head of the file to decide whether this is a board JSON
    // rather than a scene snapshot. Both shapes count: v1 is `version: 1` with
    // a `cells` array, v2 is `version: 2` with `tabs`. (The v1-only sniff
    // silently refused every v2 board — its `cells` live inside a tab and can
    // sit past the peek window entirely.)
    void file
      .slice(0, 4096)
      .text()
      .then((peek) => {
        const looksLikeLayout =
          (/"version"\s*:\s*1/.test(peek) && /"cells"\s*:\s*\[/.test(peek))
          || (/"version"\s*:\s*2/.test(peek) && /"tabs"\s*:\s*\[/.test(peek));
        if (!looksLikeLayout) return;
        e.preventDefault();
        return file.text().then((full) => {
          try {
            const json = JSON.parse(full);
            if (isLayoutJson(json)) replaceLayout(migrateLayout(json));
          } catch (err) {
            console.warn(`[dashboard] layout drop parse failed: ${(err as Error).message}`);
          }
        });
      })
      .catch((err) => console.warn(`[dashboard] drop peek failed: ${(err as Error).message}`));
  });
}
